import React, { useState } from 'react';
import { useLanguage } from '../i18n';
import GnssApproachLayers from './GnssApproachLayers';
import { LocApproachLayerControls } from './LocApproachLayers';

const LayerPanel = ({
  basemap,
  setBasemap,
  showSwedishAirports,
  setShowSwedishAirports,
  showNavaids,
  setShowNavaids,
  showNav,
  setShowNav,
  showWaypoints,
  setShowWaypoints,
  showTmaTia,
  setShowTmaTia,
  showCtrTizRmz,
  setShowCtrTizRmz,
  showNavSurfaces,
  setShowNavSurfaces,
  shieldingAnalysis,
  showEasa,
  setShowEasa,
  showPansOps,
  setShowPansOps,
  showAnnex14,
  setShowAnnex14,
  showEtod,
  setShowEtod,
  showGeoJson,
  setShowGeoJson,
  uploadedGeoJson,
  showUserData,
  setShowUserData,
  drawnFeatures = [],
  importedFeatures = [],
  onReloadLayers,
  isAuthenticated,
  // Holding / Racetrack by Time per-type toggles
  holdingRacetrackAnalysis,
  showHoldingSurfaces,
  setShowHoldingSurfaces,
  showRacetrackSurfaces,
  setShowRacetrackSurfaces,
  // OMNI
  omniAnalysis,
  layerOmni,
  setLayerOmni,
  // OAS per-category
  oasAnalysis,
  layerOasIls1,
  setLayerOasIls1,
  layerOasIls2,
  setLayerOasIls2,
  layerOasIls2ap,
  setLayerOasIls2ap,
  layerOasSbasApv1,
  setLayerOasSbasApv1,
  layerOasSbasCat1,
  setLayerOasSbasCat1,
  // BARO/VNAV
  baroVnavAnalysis,
  layerBaro,
  setLayerBaro,
  // VSS
  vssAnalysis,
  layerVss,
  setLayerVss,
  // Base Turn
  holdingBaseAnalysis,
  layerBaseTurn,
  setLayerBaseTurn,
  // RNAV Holding
  rnavHoldingAnalysis,
  showRnavHoldingSurfaces,
  setShowRnavHoldingSurfaces,
  // RNP Holding
  rnpHoldingAnalysis,
  showRnpHoldingSurfaces,
  setShowRnpHoldingSurfaces,
  // VOR/DME Holding
  vorDmeHoldingAnalysis,
  showVorDmeHoldingSurfaces,
  setShowVorDmeHoldingSurfaces,
  // Conv. Holding / Racetrack by Distance
  showHoldingDistSurfaces,
  setShowHoldingDistSurfaces,
  // MSA
  msaAnalysis,
  showMsaSurfaces,
  setShowMsaSurfaces,
  // TAA
  taaAnalysis,
  showTaaSectors,
  setShowTaaSectors,
  // Circling
  circlingAnalysis,
  showCirclingAreas,
  setShowCirclingAreas,
  showCirclingCats,
  setShowCirclingCats,
  // LOC
  locAnalysis, locLayerState, setLocLayerState,
  // GNSS APCH
  gnssApchAnalysis,
  showGnssApchSurfaces,
  setShowGnssApchSurfaces,
  showGnssApchLayers,
  setShowGnssApchLayers,
}) => {
  const { t, language } = useLanguage();

  const [enrLoginWarn, setEnrLoginWarn] = useState(false);
  const [navLoginWarn, setNavLoginWarn] = useState(false);
  const [waypointsLoginWarn, setWaypointsLoginWarn] = useState(false);
  const [tmaTiaLoginWarn, setTmaTiaLoginWarn] = useState(false);
  const [ctrTizRmzLoginWarn, setCtrTizRmzLoginWarn] = useState(false);
  const [easaLoginWarn, setEasaLoginWarn] = useState(false);
  const [pansOpsLoginWarn, setPansOpsLoginWarn] = useState(false);
  const [annex14LoginWarn, setAnnex14LoginWarn] = useState(false);
  const [etodLoginWarn, setEtodLoginWarn] = useState(false);
  const [inlineWarn, setInlineWarn] = useState(null); // { id: string, type: 'login' | 'analysis' }

  const showInlineWarn = (id, type) => {
    setInlineWarn({ id, type });
    setTimeout(() => setInlineWarn(null), 4000);
  };

  // Kombinera ritat och importerat data för räknare
  const numDrawn = drawnFeatures.length;
  const numImported = importedFeatures.length;

  return (
    <div className="layer-panel-content">
      {/* Reload button at the top */}
      {onReloadLayers && (
        <div style={{ marginBottom: '15px' }}>
          <button
            onClick={onReloadLayers}
            style={{
              width: '100%',
              padding: '10px',
              background: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '14px'
            }}
            onMouseOver={(e) => e.target.style.background = '#1565c0'}
            onMouseOut={(e) => e.target.style.background = '#1976d2'}
          >
            🔄 {t('reloadLayers') || 'Ladda om lager'}
          </button>
        </div>
      )}
      
      {/* Bakgrundslager */}
      <div className="layer-section">
        <h3>{t('backgroundLayers') || 'Bakgrundslager'}</h3>
        <label className="layer-option">
          <input
            type="radio"
            name="basemap"
            checked={basemap === 'osm'}
            onChange={() => setBasemap('osm')}
          />
          <span>{t('openStreetMap') || 'OpenStreetMap'}</span>
        </label>
        <label className="layer-option">
          <input
            type="radio"
            name="basemap"
            checked={basemap === 'cartodb'}
            onChange={() => setBasemap('cartodb')}
          />
          <span>{t('cartoDBLight') || 'CartoDB Light'}</span>
        </label>
        <label className="layer-option">
          <input
            type="radio"
            name="basemap"
            checked={basemap === 'esri'}
            onChange={() => setBasemap('esri')}
          />
          <span>{t('esriAerial') || 'Flygbilder ESRI'}</span>
        </label>
        <label className="layer-option">
          <input
            type="radio"
            name="basemap"
            checked={basemap === 'railway'}
            onChange={() => setBasemap('railway')}
          />
          <span>{t('openRailwayMap') || 'OpenRailwayMap'}</span>
        </label>
      </div>

      <hr className="layer-divider" />

      {/* === Användardata === */}
      <div className="layer-section">
        <h3>{t('userdataLayers') || 'Användarens egen datalager'}</h3>
        <label className="layer-option">
          <input
            type="checkbox"
            checked={!!showUserData}
            onChange={() => setShowUserData(!showUserData)}
          />
          <span>
            {t('userDrawnLayer') || 'Ritad data'}
            {numImported > 0 ? ` + ${t('importedGeoJsonLayer') || 'Importerad GeoJSON'}` : ''}
          </span>
        </label>
        <div style={{ marginLeft: 20, marginTop: 4, fontSize: "90%", color: "#666" }}>
          {numDrawn > 0 && <div>{t('numDrawnObjects') || 'Ritad data'}: {numDrawn}</div>}
          {numImported > 0 && <div>{t('numImportedObjects') || 'Importerad GeoJSON'}: {numImported}</div>}
        </div>
        {/* Flytta GeoJSON-lagret hit, under användardata-rubriken */}
        {uploadedGeoJson && (
          <label className="layer-option">
            <input
              type="checkbox"
              checked={!!showGeoJson}
              onChange={() => setShowGeoJson(!showGeoJson)}
            />
            <span>{t('geojson') || 'GeoJSON-lager'}</span>
          </label>
        )}
      </div>

      <hr className="layer-divider" />

      {/* Informationslager */}
      <div className="layer-section">
        <h3>{t('informationLayers') || 'Informationslager'}</h3>
        <label className="layer-option">
          <input
            type="checkbox"
            checked={!!showSwedishAirports}
            onChange={() => setShowSwedishAirports(!showSwedishAirports)}
          />
          <span>{t('swedishAirports') || 'Svenska flygplatser'}</span>
        </label>
        <label className="layer-option">
          <input
            type="checkbox"
            checked={!!showNavaids}
            onChange={() => {
              if (!isAuthenticated) {
                setEnrLoginWarn(true);
                setTimeout(() => setEnrLoginWarn(false), 4000);
                return;
              }
              setShowNavaids(!showNavaids);
            }}
          />
          <span>{t('enrNavaids') || 'ENR NAVAIDS'}</span>
        </label>
        {enrLoginWarn && (
          <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
            ⚠ {t('navInputsRequiresLogin') || 'Log in to see nav inputs on this layer.'}
          </p>
        )}
        <label className="layer-option">
          <input
            type="checkbox"
            checked={!!showNav}
            onChange={() => {
              if (!isAuthenticated) {
                setNavLoginWarn(true);
                setTimeout(() => setNavLoginWarn(false), 4000);
                return;
              }
              setShowNav(!showNav);
            }}
          />
          <span>{t('nav') || 'NAV'}</span>
        </label>
        {navLoginWarn && (
          <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
            ⚠ {t('navInputsRequiresLogin') || 'Log in to see nav inputs on this layer.'}
          </p>
        )}
        <label className="layer-option">
          <input
            type="checkbox"
            checked={!!showWaypoints}
            onChange={() => {
              if (!isAuthenticated) {
                setWaypointsLoginWarn(true);
                setTimeout(() => setWaypointsLoginWarn(false), 4000);
                return;
              }
              setShowWaypoints(!showWaypoints);
            }}
          />
          <span>{t('waypoints') || 'Waypoints'}</span>
        </label>
        {waypointsLoginWarn && (
          <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
            ⚠ {t('loginToSeeInputs') || 'Log in to see inputs on this layer.'}
          </p>
        )}
        <label className="layer-option">
          <input
            type="checkbox"
            checked={!!showTmaTia}
            onChange={() => {
              if (!isAuthenticated) {
                setTmaTiaLoginWarn(true);
                setTimeout(() => setTmaTiaLoginWarn(false), 4000);
                return;
              }
              setShowTmaTia(!showTmaTia);
            }}
          />
          <span>{t('tmaTia') || 'TMA/TIA'}</span>
        </label>
        {tmaTiaLoginWarn && (
          <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
            ⚠ {t('loginToSeeInputs') || 'Log in to see inputs on this layer.'}
          </p>
        )}
        <label className="layer-option">
          <input
            type="checkbox"
            checked={!!showCtrTizRmz}
            onChange={() => {
              if (!isAuthenticated) {
                setCtrTizRmzLoginWarn(true);
                setTimeout(() => setCtrTizRmzLoginWarn(false), 4000);
                return;
              }
              setShowCtrTizRmz(!showCtrTizRmz);
            }}
          />
          <span>{t('ctrTizRmz') || 'CTR/TIZ/RMZ'}</span>
        </label>
        {ctrTizRmzLoginWarn && (
          <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
            ⚠ {t('loginToSeeInputs') || 'Log in to see inputs on this layer.'}
          </p>
        )}
        {/* GeoJSON-lagret är nu borttaget härifrån */}
      </div>

      <hr className="layer-divider" />

      {/* Flygplatsens skyddsområden */}
      <div className="layer-section">
        <h3>{t('protectionAreas')}</h3>
        <label className="layer-option">
          <input
            type="checkbox"
            checked={!!showNavSurfaces}
            onChange={() => {
              if (!isAuthenticated) {
                showInlineWarn('nav', 'login');
                return;
              }
              if (!shieldingAnalysis) {
                showInlineWarn('nav', 'analysis');
                return;
              }
              setShowNavSurfaces && setShowNavSurfaces(!showNavSurfaces);
            }}
          />
          <span>{showNavSurfaces ? (t('shieldingToggleHide') || 'Hide NAV surfaces') : (t('shieldingToggleShow') || 'Show NAV surfaces')}</span>
        </label>
        {inlineWarn?.id === 'nav' && (
          <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
            ⚠ {inlineWarn.type === 'login' ? (t('loginToSeeInputs') || 'Log in to see inputs on this layer.') : (t('runAnalysisFirst') || 'Run an analysis first to show surfaces.')}
          </p>
        )}
        <label className="layer-option">
          <input
            type="checkbox"
            checked={!!showEasa}
            onChange={() => {
              if (!isAuthenticated) {
                setEasaLoginWarn(true);
                setTimeout(() => setEasaLoginWarn(false), 4000);
                return;
              }
              setShowEasa(!showEasa);
            }}
          />
          <span>{t('easa') || 'EASA'}</span>
        </label>
        {easaLoginWarn && (
          <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
            ⚠ {t('loginToSeeInputs') || 'Log in to see inputs on this layer.'}
          </p>
        )}
        <label className="layer-option">
          <input
            type="checkbox"
            checked={!!showPansOps}
            onChange={() => {
              if (!isAuthenticated) {
                setPansOpsLoginWarn(true);
                setTimeout(() => setPansOpsLoginWarn(false), 4000);
                return;
              }
              setShowPansOps(!showPansOps);
            }}
          />
          <span>{t('pansOps') || 'PANS OPS'}</span>
        </label>
        {pansOpsLoginWarn && (
          <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
            ⚠ {t('loginToSeeInputs') || 'Log in to see inputs on this layer.'}
          </p>
        )}
        <label className="layer-option">
          <input
            type="checkbox"
            checked={!!showAnnex14}
            onChange={() => {
              if (!isAuthenticated) {
                setAnnex14LoginWarn(true);
                setTimeout(() => setAnnex14LoginWarn(false), 4000);
                return;
              }
              setShowAnnex14(!showAnnex14);
            }}
          />
          <span>{t('annex14') || 'Annex 14'}</span>
        </label>
        {annex14LoginWarn && (
          <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
            ⚠ {t('loginToSeeInputs') || 'Log in to see inputs on this layer.'}
          </p>
        )}
        <label className="layer-option">
          <input
            type="checkbox"
            checked={!!showEtod}
            onChange={() => {
              if (!isAuthenticated) {
                setEtodLoginWarn(true);
                setTimeout(() => setEtodLoginWarn(false), 4000);
                return;
              }
              setShowEtod(!showEtod);
            }}
          />
          <span>{t('etod') || 'eTOD'}</span>
        </label>
        {etodLoginWarn && (
          <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
            ⚠ {t('loginToSeeInputs') || 'Log in to see inputs on this layer.'}
          </p>
        )}

        {/* ── Analysis surface layers ── */}
        {setLayerOmni && (
          <>
            <label className="layer-option">
              <input
                type="checkbox"
                checked={!!layerOmni}
                onChange={() => {
                  if (!isAuthenticated) {
                    showInlineWarn('omni', 'login');
                    return;
                  }
                  if (!omniAnalysis) {
                    showInlineWarn('omni', 'analysis');
                    return;
                  }
                  setLayerOmni(!layerOmni);
                }}
              />
              <span>{t('layerShowOmni') || 'Show OMNI surfaces'}</span>
            </label>
            {inlineWarn?.id === 'omni' && (
              <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
                ⚠ {inlineWarn.type === 'login' ? (t('loginToSeeInputs') || 'Log in to see inputs on this layer.') : (t('runAnalysisFirst') || 'Run an analysis first to show surfaces.')}
              </p>
            )}
          </>
        )}
        {setLayerOasIls1 && (
          <>
            <label className="layer-option">
              <input
                type="checkbox"
                checked={!!layerOasIls1}
                onChange={() => {
                  if (!isAuthenticated) {
                    showInlineWarn('oasIls1', 'login');
                    return;
                  }
                  if (!oasAnalysis) {
                    showInlineWarn('oasIls1', 'analysis');
                    return;
                  }
                  setLayerOasIls1(!layerOasIls1);
                }}
              />
              <span>{t('layerShowOasIls1') || 'Show OAS ILS CAT I'}</span>
            </label>
            {inlineWarn?.id === 'oasIls1' && (
              <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
                ⚠ {inlineWarn.type === 'login' ? (t('loginToSeeInputs') || 'Log in to see inputs on this layer.') : (t('runAnalysisFirst') || 'Run an analysis first to show surfaces.')}
              </p>
            )}
          </>
        )}
        {setLayerOasIls2 && (
          <>
            <label className="layer-option">
              <input
                type="checkbox"
                checked={!!layerOasIls2}
                onChange={() => {
                  if (!isAuthenticated) {
                    showInlineWarn('oasIls2', 'login');
                    return;
                  }
                  if (!oasAnalysis) {
                    showInlineWarn('oasIls2', 'analysis');
                    return;
                  }
                  setLayerOasIls2(!layerOasIls2);
                }}
              />
              <span>{t('layerShowOasIls2') || 'Show OAS ILS CAT II'}</span>
            </label>
            {inlineWarn?.id === 'oasIls2' && (
              <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
                ⚠ {inlineWarn.type === 'login' ? (t('loginToSeeInputs') || 'Log in to see inputs on this layer.') : (t('runAnalysisFirst') || 'Run an analysis first to show surfaces.')}
              </p>
            )}
          </>
        )}
        {setLayerOasIls2ap && (
          <>
            <label className="layer-option">
              <input
                type="checkbox"
                checked={!!layerOasIls2ap}
                onChange={() => {
                  if (!isAuthenticated) {
                    showInlineWarn('oasIls2ap', 'login');
                    return;
                  }
                  if (!oasAnalysis) {
                    showInlineWarn('oasIls2ap', 'analysis');
                    return;
                  }
                  setLayerOasIls2ap(!layerOasIls2ap);
                }}
              />
              <span>{t('layerShowOasIls2ap') || 'Show OAS ILS CAT II AP'}</span>
            </label>
            {inlineWarn?.id === 'oasIls2ap' && (
              <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
                ⚠ {inlineWarn.type === 'login' ? (t('loginToSeeInputs') || 'Log in to see inputs on this layer.') : (t('runAnalysisFirst') || 'Run an analysis first to show surfaces.')}
              </p>
            )}
          </>
        )}
        {setLayerOasSbasApv1 && (
          <>
            <label className="layer-option">
              <input
                type="checkbox"
                checked={!!layerOasSbasApv1}
                onChange={() => {
                  if (!isAuthenticated) {
                    showInlineWarn('oasSbasApv1', 'login');
                    return;
                  }
                  if (!oasAnalysis) {
                    showInlineWarn('oasSbasApv1', 'analysis');
                    return;
                  }
                  setLayerOasSbasApv1(!layerOasSbasApv1);
                }}
              />
              <span>{t('layerShowOasSbasApv1') || 'Show OAS SBAS APV I'}</span>
            </label>
            {inlineWarn?.id === 'oasSbasApv1' && (
              <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
                ⚠ {inlineWarn.type === 'login' ? (t('loginToSeeInputs') || 'Log in to see inputs on this layer.') : (t('runAnalysisFirst') || 'Run an analysis first to show surfaces.')}
              </p>
            )}
          </>
        )}
        {setLayerOasSbasCat1 && (
          <>
            <label className="layer-option">
              <input
                type="checkbox"
                checked={!!layerOasSbasCat1}
                onChange={() => {
                  if (!isAuthenticated) {
                    showInlineWarn('oasSbasCat1', 'login');
                    return;
                  }
                  if (!oasAnalysis) {
                    showInlineWarn('oasSbasCat1', 'analysis');
                    return;
                  }
                  setLayerOasSbasCat1(!layerOasSbasCat1);
                }}
              />
              <span>{t('layerShowOasSbasCat1') || 'Show OAS SBAS CAT I'}</span>
            </label>
            {inlineWarn?.id === 'oasSbasCat1' && (
              <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
                ⚠ {inlineWarn.type === 'login' ? (t('loginToSeeInputs') || 'Log in to see inputs on this layer.') : (t('runAnalysisFirst') || 'Run an analysis first to show surfaces.')}
              </p>
            )}
          </>
        )}
        {setLayerBaro && (
          <>
            <label className="layer-option">
              <input
                type="checkbox"
                checked={!!layerBaro}
                onChange={() => {
                  if (!isAuthenticated) {
                    showInlineWarn('baro', 'login');
                    return;
                  }
                  if (!baroVnavAnalysis) {
                    showInlineWarn('baro', 'analysis');
                    return;
                  }
                  setLayerBaro(!layerBaro);
                }}
              />
              <span>{t('layerShowBaro') || 'Show BARO/VNAV surfaces'}</span>
            </label>
            {inlineWarn?.id === 'baro' && (
              <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
                ⚠ {inlineWarn.type === 'login' ? (t('loginToSeeInputs') || 'Log in to see inputs on this layer.') : (t('runAnalysisFirst') || 'Run an analysis first to show surfaces.')}
              </p>
            )}
          </>
        )}
        {setLayerVss && (
          <>
            <label className="layer-option">
              <input
                type="checkbox"
                checked={!!layerVss}
                onChange={() => {
                  if (!isAuthenticated) {
                    showInlineWarn('vss', 'login');
                    return;
                  }
                  if (!vssAnalysis) {
                    showInlineWarn('vss', 'analysis');
                    return;
                  }
                  setLayerVss(!layerVss);
                }}
              />
              <span>{t('layerShowVss') || 'Show VSS surfaces'}</span>
            </label>
            {inlineWarn?.id === 'vss' && (
              <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
                ⚠ {inlineWarn.type === 'login' ? (t('loginToSeeInputs') || 'Log in to see inputs on this layer.') : (t('runAnalysisFirst') || 'Run an analysis first to show surfaces.')}
              </p>
            )}
          </>
        )}
        {setLayerBaseTurn && (
          <>
            <label className="layer-option">
              <input
                type="checkbox"
                checked={!!layerBaseTurn}
                onChange={() => {
                  if (!isAuthenticated) {
                    showInlineWarn('baseTurn', 'login');
                    return;
                  }
                  if (!holdingBaseAnalysis) {
                    showInlineWarn('baseTurn', 'analysis');
                    return;
                  }
                  setLayerBaseTurn(!layerBaseTurn);
                }}
              />
              <span>{t('layerShowBaseTurn') || 'Show Base Turn surfaces'}</span>
            </label>
            {inlineWarn?.id === 'baseTurn' && (
              <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
                ⚠ {inlineWarn.type === 'login' ? (t('loginToSeeInputs') || 'Log in to see inputs on this layer.') : (t('runAnalysisFirst') || 'Run an analysis first to show surfaces.')}
              </p>
            )}
          </>
        )}

        {/* Conv. Holding / Racetrack (Time) layers */}
        {setShowHoldingSurfaces && (
          <>
            <label className="layer-option">
              <input
                type="checkbox"
                checked={!!showHoldingSurfaces}
                onChange={() => {
                  if (!isAuthenticated) {
                    showInlineWarn('convHolding', 'login');
                    return;
                  }
                  if (!holdingRacetrackAnalysis) {
                    showInlineWarn('convHolding', 'analysis');
                    return;
                  }
                  setShowHoldingSurfaces(!showHoldingSurfaces);
                }}
              />
              <span>{t('layerShowConvHolding') || 'Show Conv. Holding (Time)'}</span>
            </label>
            {inlineWarn?.id === 'convHolding' && (
              <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
                ⚠ {inlineWarn.type === 'login' ? (t('loginToSeeInputs') || 'Log in to see inputs on this layer.') : (t('runAnalysisFirst') || 'Run an analysis first to show surfaces.')}
              </p>
            )}
          </>
        )}
        {setShowRacetrackSurfaces && (
          <>
            <label className="layer-option">
              <input
                type="checkbox"
                checked={!!showRacetrackSurfaces}
                onChange={() => {
                  if (!isAuthenticated) {
                    showInlineWarn('convRacetrack', 'login');
                    return;
                  }
                  if (!holdingRacetrackAnalysis) {
                    showInlineWarn('convRacetrack', 'analysis');
                    return;
                  }
                  setShowRacetrackSurfaces(!showRacetrackSurfaces);
                }}
              />
              <span>{t('layerShowConvRacetrack') || 'Show Conv. Racetrack (Time)'}</span>
            </label>
            {inlineWarn?.id === 'convRacetrack' && (
              <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
                ⚠ {inlineWarn.type === 'login' ? (t('loginToSeeInputs') || 'Log in to see inputs on this layer.') : (t('runAnalysisFirst') || 'Run an analysis first to show surfaces.')}
              </p>
            )}
          </>
        )}

        {/* Conv. Holding / Racetrack by Distance layer */}
        {setShowHoldingDistSurfaces && (
          <>
            <label className="layer-option">
              <input
                type="checkbox"
                checked={!!showHoldingDistSurfaces}
                onChange={() => {
                  if (!isAuthenticated) {
                    showInlineWarn('convHoldingDist', 'login');
                    return;
                  }
                  if (!holdingRacetrackAnalysis) {
                    showInlineWarn('convHoldingDist', 'analysis');
                    return;
                  }
                  setShowHoldingDistSurfaces(!showHoldingDistSurfaces);
                }}
              />
              <span>{t('layerShowConvHoldingDist') || 'Show Conv. Holding/Racetrack (Distance)'}</span>
            </label>
            {inlineWarn?.id === 'convHoldingDist' && (
              <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
                ⚠ {inlineWarn.type === 'login' ? (t('loginToSeeInputs') || 'Log in to see inputs on this layer.') : (t('runAnalysisFirst') || 'Run an analysis first to show surfaces.')}
              </p>
            )}
          </>
        )}

        {/* RNAV Holding layer */}
        {setShowRnavHoldingSurfaces && (
          <>
            <label className="layer-option">
              <input
                type="checkbox"
                checked={!!showRnavHoldingSurfaces}
                onChange={() => {
                  if (!isAuthenticated) {
                    showInlineWarn('rnavHolding', 'login');
                    return;
                  }
                  if (!rnavHoldingAnalysis) {
                    showInlineWarn('rnavHolding', 'analysis');
                    return;
                  }
                  setShowRnavHoldingSurfaces(!showRnavHoldingSurfaces);
                }}
              />
              <span>{t('layerShowRnavHolding') || 'Show RNAV Holding surfaces'}</span>
            </label>
            {inlineWarn?.id === 'rnavHolding' && (
              <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
                ⚠ {inlineWarn.type === 'login' ? (t('loginToSeeInputs') || 'Log in to see inputs on this layer.') : (t('runAnalysisFirst') || 'Run an analysis first to show surfaces.')}
              </p>
            )}
          </>
        )}

        {/* RNP Holding layer */}
        {setShowRnpHoldingSurfaces && (
          <>
            <label className="layer-option">
              <input
                type="checkbox"
                checked={!!showRnpHoldingSurfaces}
                onChange={() => {
                  if (!isAuthenticated) {
                    showInlineWarn('rnpHolding', 'login');
                    return;
                  }
                  if (!rnpHoldingAnalysis) {
                    showInlineWarn('rnpHolding', 'analysis');
                    return;
                  }
                  setShowRnpHoldingSurfaces(!showRnpHoldingSurfaces);
                }}
              />
              <span>{t('layerShowRnpHolding') || 'Show RNP Holding surfaces'}</span>
            </label>
            {inlineWarn?.id === 'rnpHolding' && (
              <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
                ⚠ {inlineWarn.type === 'login' ? (t('loginToSeeInputs') || 'Log in to see inputs on this layer.') : (t('runAnalysisFirst') || 'Run an analysis first to show surfaces.')}
              </p>
            )}
          </>
        )}

        {/* VOR/DME Holding layer */}
        {setShowVorDmeHoldingSurfaces && (
          <>
            <label className="layer-option">
              <input
                type="checkbox"
                checked={!!showVorDmeHoldingSurfaces}
                onChange={() => {
                  if (!isAuthenticated) {
                    showInlineWarn('vorDmeHolding', 'login');
                    return;
                  }
                  if (!vorDmeHoldingAnalysis) {
                    showInlineWarn('vorDmeHolding', 'analysis');
                    return;
                  }
                  setShowVorDmeHoldingSurfaces(!showVorDmeHoldingSurfaces);
                }}
              />
              <span>{t('layerShowVorDmeHolding') || 'Show VOR/DME Holding surfaces'}</span>
            </label>
            {inlineWarn?.id === 'vorDmeHolding' && (
              <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
                ⚠ {inlineWarn.type === 'login' ? (t('loginToSeeInputs') || 'Log in to see inputs on this layer.') : (t('runAnalysisFirst') || 'Run an analysis first to show surfaces.')}
              </p>
            )}
          </>
        )}

        {/* MSA Sector layer */}
        {setShowMsaSurfaces && (
          <>
            <label className="layer-option">
              <input
                type="checkbox"
                checked={!!showMsaSurfaces}
                onChange={() => {
                  if (!isAuthenticated) {
                    showInlineWarn('msa', 'login');
                    return;
                  }
                  if (!msaAnalysis) {
                    showInlineWarn('msa', 'analysis');
                    return;
                  }
                  setShowMsaSurfaces(!showMsaSurfaces);
                }}
              />
              <span>{t('layerShowMsa') || 'Show MSA sectors'}</span>
            </label>
            {inlineWarn?.id === 'msa' && (
              <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
                ⚠ {inlineWarn.type === 'login' ? (t('loginToSeeInputs') || 'Log in to see inputs on this layer.') : (t('runAnalysisFirst') || 'Run an analysis first to show surfaces.')}
              </p>
            )}
          </>
        )}

        {/* TAA Sector layer */}
        {setShowTaaSectors && (
          <>
            <label className="layer-option">
              <input
                type="checkbox"
                checked={!!showTaaSectors}
                onChange={() => {
                  if (!isAuthenticated) {
                    showInlineWarn('taa', 'login');
                    return;
                  }
                  if (!taaAnalysis?.results?.length) {
                    showInlineWarn('taa', 'analysis');
                    return;
                  }
                  setShowTaaSectors(!showTaaSectors);
                }}
              />
              <span>{t('layerShowTaa') || 'Show TAA sectors'}</span>
            </label>
            {inlineWarn?.id === 'taa' && (
              <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
                ⚠ {inlineWarn.type === 'login' ? (t('loginToSeeInputs') || 'Log in to see inputs on this layer.') : (t('runAnalysisFirst') || 'Run an analysis first to show surfaces.')}
              </p>
            )}
          </>
        )}

        {/* Circling protected areas layer */}
        {setShowCirclingAreas && (
          <>
            <label className="layer-option">
              <input
                type="checkbox"
                checked={!!showCirclingAreas}
                onChange={() => {
                  if (!isAuthenticated) {
                    showInlineWarn('circling', 'login');
                    return;
                  }
                  if (!circlingAnalysis?.results?.length) {
                    showInlineWarn('circling', 'analysis');
                    return;
                  }
                  setShowCirclingAreas(!showCirclingAreas);
                }}
              />
              <span>{t('layerShowCircling') || 'Show Visual Circling'}</span>
            </label>
            {inlineWarn?.id === 'circling' && (
              <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
                ⚠ {inlineWarn.type === 'login' ? (t('loginToSeeInputs') || 'Log in to see inputs on this layer.') : (t('runAnalysisFirst') || 'Run an analysis first to show surfaces.')}
              </p>
            )}
            {showCirclingAreas && setShowCirclingCats && showCirclingCats && (
              <div style={{ marginLeft: 22, marginTop: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {['A', 'B', 'C', 'D'].map(cat => (
                  <label key={cat} className="layer-option" style={{ fontSize: '12px' }}>
                    <input
                      type="checkbox"
                      checked={showCirclingCats[cat] !== false}
                      onChange={() =>
                        setShowCirclingCats(prev => ({ ...prev, [cat]: !prev[cat] }))
                      }
                    />
                    <span>Cat {cat}</span>
                  </label>
                ))}
              </div>
            )}
          </>
        )}

        {/* GNSS Approach protected areas layer */}
        {setShowGnssApchSurfaces && (
          <>
            <label className="layer-option">
              <input
                type="checkbox"
                checked={!!showGnssApchSurfaces}
                onChange={() => {
                  if (!isAuthenticated) {
                    showInlineWarn('gnssApch', 'login');
                    return;
                  }
                  if (!gnssApchAnalysis?.results?.length) {
                    showInlineWarn('gnssApch', 'analysis');
                    return;
                  }
                  setShowGnssApchSurfaces(!showGnssApchSurfaces);
                }}
              />
              <span>{t('layerShowGnssApch') || 'Show GNSS Apch'}</span>
            </label>
            {inlineWarn?.id === 'gnssApch' && (
              <p style={{ margin: '2px 0 6px 22px', fontSize: '12px', color: '#b45309' }}>
                ⚠ {inlineWarn.type === 'login' ? (t('loginToSeeInputs') || 'Log in to see inputs on this layer.') : (t('runAnalysisFirst') || 'Run an analysis first to show surfaces.')}
              </p>
            )}
            {showGnssApchSurfaces && setShowGnssApchLayers && showGnssApchLayers && (
              <GnssApproachLayers results={gnssApchAnalysis?.results || []} state={showGnssApchLayers}
                onChange={setShowGnssApchLayers} language={language} />
            )}
          </>
        )}
        {isAuthenticated && setLocLayerState && locLayerState && <LocApproachLayerControls
          analysis={locAnalysis} state={locLayerState} onChange={setLocLayerState} language={language} />}
      </div>
    </div>
  );
};

export default LayerPanel;
